import React, { useState } from "react";
import PropTypes from "prop-types";
import Papa from "papaparse";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import { getFirestore, setDoc, doc, collection, getDocs } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

export default function BulkImportDialog({ isOpen, toggle }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [skippedRows, setSkippedRows] = useState([]);
  const db = getFirestore();

  const handleFileUpload = (e) => {
    setFile(e.target.files[0]);
  };

  const delay = (ms) => new Promise((resolve) => {
    setTimeout(() => {
      resolve();
    }, ms);
  });
  

  const retryWithBackoff = async (fn, retries = 10, initialDelayMs = 500) => {
    let attempt = 0;
    const execute = async () => {
      try {
        return await fn();
      } catch (error) {
        if (attempt >= retries - 1) throw error;
        const backoff = initialDelayMs * (2 ** attempt) + Math.random() * 100; // Replaced Math.pow with **
        await delay(backoff);
        attempt += 1;
        return execute();
      }
    };
    return execute();
  };
  
  

  const handleImport = async () => {
    if (!file) {
      alert("Please select a CSV file to import.");
      return;
    }
  
    setLoading(true);
    setSkippedRows([]);
  
    try {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: async (results) => {
          const rows = results.data;
          const skipped = [];
          const tasks = rows.map((row, index) => {
            const {
              "Email Address": EmailAddress,
              "First Name": FirstName,
              Surname,
              "Address Line 1": AddressLine1,
              "Address Line 2": AddressLine2,
              "Suburb/Town": Suburb,
              State,
              Postcode,
              "Contact Phone": ContactPhone,
              Gender,
              "Date of Birth": DateOfBirth,
              "Squash Player": SquashPlayer,
              "Racquetball Player": RacquetballPlayer,
            } = row;
  
            if (!FirstName || !Surname || !EmailAddress) {
              console.warn(`Row ${index + 1} skipped: Missing required fields`, row);
              skipped.push({ rowNumber: index + 1, reason: "Missing required fields", row });
              return null;
            }
  
            // Validate and convert Date of Birth
            let formattedBirthday = "";
            if (DateOfBirth && DateOfBirth.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
              const parts = DateOfBirth.split("/");
              const [day, month, year] = parts.map((p) => p.padStart(2, "0")); // Ensure 2 digits for day/month
              formattedBirthday = `${year}-${month}-${day}`; // Convert to YYYY-MM-DD
            } else if (DateOfBirth) {
              console.warn(`Row ${index + 1} skipped: Invalid date format`, row);
              skipped.push({ rowNumber: index + 1, reason: "Invalid date format", row });
              return null;
            }
  
            let formattedPhoneNumber = ContactPhone || "";
            if (formattedPhoneNumber) {
              if (formattedPhoneNumber.startsWith("61")) {
                formattedPhoneNumber = `+${formattedPhoneNumber}`;
              } else if (formattedPhoneNumber.startsWith("0")) {
                formattedPhoneNumber = `+61${formattedPhoneNumber.slice(1)}`;
              } else {
                formattedPhoneNumber = `+61${formattedPhoneNumber}`;
              }
            }
  
            return async () => {
              try {
                const searchCustomer = httpsCallable(functions, "searchSquareCustomer");
                const payload = { email: EmailAddress, phoneNumber: formattedPhoneNumber };
  
                const searchResponse = await retryWithBackoff(() =>
                  searchCustomer(payload)
                );
  
                const existingCustomers = searchResponse.data.customers || [];
                const isDuplicate = existingCustomers.length > 0;
  
                let squareCustomerId;
  
                if (isDuplicate) {
                  squareCustomerId = existingCustomers[0].id;
                } else {
                  const createCustomer = httpsCallable(functions, "createSquareCustomer");
  
                  const createResponse = await retryWithBackoff(() =>
                    createCustomer({
                      email: EmailAddress,
                      firstName: FirstName,
                      lastName: Surname,
                      phoneNumber: formattedPhoneNumber,
                      birthday: formattedBirthday, // Use the normalized date
                      address: {
                        addressLine1: AddressLine1 || "",
                        addressLine2: AddressLine2 || "",
                        city: Suburb || "",
                        state: State || "",
                        postalCode: Postcode || "",
                      },
                      referenceId: "BulkImport",
                    })
                  );
                  squareCustomerId = createResponse.data.customerId;
                }
  
                const memberData = {
                  firstName: FirstName,
                  lastName: Surname,
                  phoneNumber: formattedPhoneNumber,
                  email: EmailAddress,
                  address: {
                    addressLine1: AddressLine1 || "",
                    addressLine2: AddressLine2 || "",
                    city: Suburb || "",
                    state: State || "",
                    postalCode: Postcode || "",
                  },
                  gender: Gender || "",
                  birthday: formattedBirthday, // Save as `birthday`
                  squashPlayer: SquashPlayer === "X",
                  racquetballPlayer: RacquetballPlayer === "X",
                  squareCustomerId,
                };
  
                const docRef = doc(db, "members", squareCustomerId);
                await setDoc(docRef, memberData, { merge: true });
              } catch (error) {
                console.error(`Error processing row ${index + 1}:`, error.message);
                skipped.push({
                  rowNumber: index + 1,
                  reason: `Error: ${error.message}`,
                  row,
                });
              }
            };
          });
  
          await Promise.all(tasks.map((task) => task && task()));
  
          setSkippedRows(skipped.sort((a, b) => a.rowNumber - b.rowNumber));
          if (skipped.length === 0) {
            alert("All members imported successfully.");
          } else {
            alert("Some rows were skipped. Check the list for details.");
          }
        },
        error: (error) => {
          console.error("Error parsing CSV file:", error);
          alert("Error parsing the file. Please check the format and try again.");
        },
      });
    } catch (error) {
      console.error("Error importing members:", error);
      alert("Error importing members. Please check the console for details.");
    } finally {
      setLoading(false);
    }
  };
  
  
  
  
  const handleExport = async () => {
    setLoading(true);
  
    try {
      const membersCollection = collection(db, "members");
      const membersSnapshot = await getDocs(membersCollection);
      const members = membersSnapshot.docs.map((doc) => doc.data());
  
      const formattedData = members.map((member) => {
        let dateOfBirth = "";
  
        // Validate and format the dateOfBirth field
        if (member.birthday && !Number.isNaN(new Date(member.birthday).getTime())) {
          dateOfBirth = new Date(member.birthday).toLocaleDateString("en-GB");
        } else if (member.birthday) {
          dateOfBirth = "Invalid Date";
        }
  
        return {
          "First Name": member.firstName || "",
          Surname: member.lastName || "",
          "Address Line 1": member.address?.addressLine1 || "",
          "Address Line 2": member.address?.addressLine2 || "",
          "Suburb/Town": member.address?.city || "",
          State: member.address?.state || "",
          Postcode: member.address?.postalCode || "",
          "Contact Phone": member.phoneNumber || "",
          "Email Address": member.email || "",
          Gender: member.gender || "",
          "Date of Birth": dateOfBirth,
          "Squash Player": member.squashPlayer ? "X" : "",
          "Racquetball Player": member.racquetballPlayer ? "X" : "",
        };
      });
  
      const csv = Papa.unparse(formattedData);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "members_export.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting members:", error);
      alert("Error exporting members. Please check the console for details.");
    } finally {
      setLoading(false);
    }
  };
  
  

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        setFile(null);
        setSkippedRows([]);
        toggle();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Bulk Import/Export Members</DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          To import members, first export the current list to ensure the correct file format. Use the exported CSV as a template for importing members. Each member must have a unique email AND contact phone number.
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Button
            variant="contained"
            onClick={handleExport}
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : "Export Members"}
          </Button>
          <input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            disabled={loading}
            style={{ marginLeft: "16px" }}
          />
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={loading || !file}
          >
            {loading ? <CircularProgress size={20} /> : "Import Members"}
          </Button>
        </Box>
        {skippedRows.length > 0 && (
          <Box>
            <Typography variant="h6">Skipped Rows:</Typography>
            <List>
              {skippedRows.map((skipped, index) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={`Row ${skipped.rowNumber}: ${skipped.reason}`}
                    secondary={JSON.stringify(skipped.row)}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setFile(null);
            setSkippedRows([]);
            toggle();
          }}
          disabled={loading}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

BulkImportDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
};